import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RouteErrorBoundary } from '../components/ErrorBoundary';
import { ProtectedLayout } from './ProtectedLayout';
import { LoginScreen } from '../app/components/LoginScreen';
import { RegisterScreen } from '../app/components/RegisterScreen';
import { ForgotPasswordScreen } from '../app/components/ForgotPasswordScreen';

// Pages will be imported here as we create them
// Lazy loading can be added later for optimization
import { MapPage } from '../pages/MapPage';
import { ActivityPage } from '../pages/ActivityPage';
import { WalletPage } from '../pages/WalletPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { ProfilePage } from '../pages/ProfilePage';
import { PhoneCollectionScreen } from '../app/components/PhoneCollectionScreen';
import { DeleteAccountPage } from '../pages/DeleteAccountPage';

/**
 * Application Router Configuration
 *
 * Route Structure:
 * - / → Redirect to /app/map
 * - /login → Login screen
 * - /register → Register screen
 * - /app → Protected layout (requires auth)
 *   - /app/map → Map view
 *   - /app/location/:locationId → Map with location detail sheet
 *   - /app/location/:locationId/chargepoint/:chargepointId → Map with chargepoint detail sheet
 *   - /app/activity → Activity list view
 *   - /app/activity/charging/:transactionId → Live charging status
 *   - /app/activity/summary/:transactionId → Transaction summary
 *   - /app/wallet → Wallet view with recharge modal
 *   - /app/notifications → Notifications view
 *   - /app/profile → Profile view with sub-sheets
 * - * → Catch-all redirect to /app/map
 *
 * Navigation Strategy:
 * - Location/ChargePoint/Activity details → Route params (for deep linking and history)
 * - Temporary UI state (filter, QR) → Query parameters
 * Auth Strategy: ProtectedLayout checks auth and redirects to /login if needed
 */
export const router = createBrowserRouter([
  // Root redirect to home
  {
    path: '/',
    element: <Navigate to="/app/map" replace />,
    errorElement: <RouteErrorBoundary />,
  },

  // Auth routes (public)
  {
    path: '/login',
    element: <LoginScreen />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/register',
    element: <RegisterScreen />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordScreen />,
    errorElement: <RouteErrorBoundary />,
  },

  // Public account deletion info page (Play Store requirement)
  {
    path: '/delete-account',
    element: <DeleteAccountPage />,
    errorElement: <RouteErrorBoundary />,
  },

  // Protected app routes
  {
    path: '/app',
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Default to map view
      {
        index: true,
        element: <Navigate to="/app/map" replace />,
      },
      // Main app pages
      {
        path: 'map',
        element: <MapPage />,
      },
      // Location detail route (shows map with location sheet)
      {
        path: 'location/:locationId',
        element: <MapPage />,
      },
      // ChargePoint detail route (shows map with chargepoint sheet)
      {
        path: 'location/:locationId/chargepoint/:chargepointId',
        element: <MapPage />,
      },
      {
        path: 'activity',
        element: <ActivityPage />,
      },
      {
        path: 'activity/charging/:transactionId',
        element: <ActivityPage />,
      },
      {
        path: 'activity/summary/:transactionId',
        element: <ActivityPage />,
      },
      {
        path: 'wallet',
        element: <WalletPage />,
      },
      {
        path: 'notifications',
        element: <NotificationsPage />,
      },
      {
        path: 'phone-collection',
        element: <PhoneCollectionScreen />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
    ],
  },

  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/app/map" replace />,
    errorElement: <RouteErrorBoundary />,
  },
]);
